/**
 * How an order's delivery timing reads on the dashboard.
 *
 * The server stores `deliverySchedule` ("Express" | "Scheduled") and, only for
 * a scheduled order, `scheduledDelivery: { windowStart, windowEnd, timezone, label }`. Anything
 * else (no value, a legacy "Scheduled" with no window) is an express order,
 * because that is how the server treated it.
 */

export const DELIVERY_TIMEZONE = "Africa/Kampala";

const partsOf = (date, timeZone, options) =>
  Object.fromEntries(
    new Intl.DateTimeFormat("en-US", { timeZone, ...options })
      .formatToParts(date)
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, part.value])
  );

/** Intl throws RangeError on an unknown zone, and this runs inside a render. */
const safeZone = (timeZone) => {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone });
    return timeZone;
  } catch (_) {
    return DELIVERY_TIMEZONE;
  }
};

const validDate = (value) => {
  const date = value ? new Date(value) : null;
  return date && Number.isFinite(date.getTime()) ? date : null;
};

/** "Thu 18 Sep, 10:00 to 10:30" for a window, or null when there is none. */
export const formatDeliveryWindow = (windowStart, windowEnd, timeZone = DELIVERY_TIMEZONE) => {
  const start = validDate(windowStart);
  const end = validDate(windowEnd);
  if (!start || !end) return null;
  const zone = safeZone(timeZone);
  const clock = { hour: "2-digit", minute: "2-digit", hourCycle: "h23" };
  const day = partsOf(start, zone, { weekday: "short", day: "numeric", month: "short" });
  const from = partsOf(start, zone, clock);
  const to = partsOf(end, zone, clock);
  return `${day.weekday} ${day.day} ${day.month}, ${from.hour}:${from.minute} to ${to.hour}:${to.minute}`;
};

/**
 * { scheduled, label, timeZone } for an order, e.g. "Express" or
 * "Scheduled: Thu 18 Sep, 10:00 to 10:30".
 *
 * The server stores `scheduledDelivery.label` (market timezone) at create;
 * that wins so every surface reads the same words. Older orders without it
 * are formatted here from the window.
 */
export const describeDeliverySchedule = (order, timeZone) => {
  const scheduledDelivery = order?.scheduledDelivery;
  const zone = scheduledDelivery?.timezone || timeZone || DELIVERY_TIMEZONE;
  const stored = typeof scheduledDelivery?.label === "string" ? scheduledDelivery.label.trim() : "";
  const window =
    stored && validDate(scheduledDelivery?.windowStart)
      ? stored
      : formatDeliveryWindow(scheduledDelivery?.windowStart, scheduledDelivery?.windowEnd, zone);
  return window
    ? { scheduled: true, label: `Scheduled: ${window}`, timeZone: zone }
    : { scheduled: false, label: "Express", timeZone: zone };
};

export default describeDeliverySchedule;
